import Markdown from "react-markdown"
import rehypeKatex from "rehype-katex"
import remarkMath from "remark-math"
import { type JSX, useEffect, useRef, useState } from "react"
import { JobsDialog, isRunning, useJobs } from "./agent_jobs"
import {
  type AgentMessageRecord,
  type AgentPresence,
  type AgentRecord,
  fetchAgentMessages,
  fetchAgents,
  sendAgentMessage,
  startAgent,
  updateAgent,
  wrapUpAgent,
} from "./api"

const POLL_INTERVAL_MS = 2000

// A transcript row as the pane holds it: server rows plus optimistic user
// turns that haven't round-tripped yet (negative ids until the server
// assigns a real one). failed marks a turn the runtime refused.
type LocalTurn = AgentMessageRecord & { failed?: boolean }

// One agent, as a chat. The transcript comes from the server (user turns are
// written on send; agent turns are projected from the runtime's event
// stream) and is polled on a short tick, so the agent's replies appear as
// they happen. The header carries the agent's identity — click the name to
// rename it.
export function AgentChat({
  agentsUrl,
  agentId,
  filesUrl,
  fileUrl,
  onAgentChanged,
  onBack,
}: {
  agentsUrl: string
  agentId: string
  filesUrl: string
  fileUrl: string
  // Reports renames upward so the rail's agent sublist stays current.
  onAgentChanged?: (agent: AgentRecord) => void
  onBack: () => void
}): JSX.Element {
  const [agent, setAgent] = useState<AgentRecord | null>(null)
  const [missing, setMissing] = useState(false)
  const [messages, setMessages] = useState<LocalTurn[]>([])
  // What the agent is doing, as the last poll found it.
  const [presence, setPresence] = useState<AgentPresence | null>(null)
  // A turn we have sent that nothing has answered yet. The runtime takes a
  // moment to start a round and report the first status, and a message that
  // lands on visible silence reads as a message that went nowhere — so we say
  // "Thinking" on our own account until the agent says something better.
  const [awaitingReply, setAwaitingReply] = useState(false)
  const [draft, setDraft] = useState("")
  const [problem, setProblem] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [jobsOpen, setJobsOpen] = useState(false)
  const { jobs, failed: jobsFailed, refresh: refreshJobs } = useJobs(filesUrl, agentId)
  // Only what is going right now. A finished job is history; this menu
  // exists to answer what the agent is doing.
  const running = jobs.filter(isRunning)
  const statusLine = presenceLine(presence, awaitingReply)
  // Recomputed on every poll, which is what keeps it moving: the poll hands
  // back a new presence object every couple of seconds whether or not anything
  // changed.
  const statusAge = presence?.status_live ? elapsedSince(presence.status_at) : null
  const lastIdRef = useRef(0)
  const tempIdRef = useRef(-1)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    void fetchAgents(agentsUrl).then((loaded) => {
      if (cancelled) return
      const found = loaded?.find((candidate) => candidate.id === agentId)
      if (found) setAgent(found)
      else setMissing(true)
    })
    return () => {
      cancelled = true
    }
  }, [agentsUrl, agentId])

  // Poll for new transcript rows. The cursor lives in a ref so the interval
  // callback always sees the latest without re-arming the timer.
  useEffect(() => {
    let cancelled = false

    async function pull(): Promise<void> {
      const batch = await fetchAgentMessages(agentsUrl, agentId, lastIdRef.current)
      if (cancelled || !batch) return
      // Before the early return: an agent that is working quietly answers with
      // no messages at all, which is exactly when the status line is the only
      // thing on screen that moves.
      setPresence(batch)
      if (batch.messages.length === 0) return
      lastIdRef.current = batch.last_id
      // Something came back, so our own placeholder has done its job — from
      // here the agent's own status speaks for it.
      setAwaitingReply(false)
      // A just-sent turn can arrive by poll before its POST returns; ids
      // already present are dropped so it can't show twice.
      setMessages((current) => [
        ...current,
        ...batch.messages.filter((incoming) => !current.some((m) => m.id === incoming.id)),
      ])
    }

    void pull()
    const timer = setInterval(() => void pull(), POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [agentsUrl, agentId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" })
  }, [messages.length])

  function absorb(result: AgentMessageRecord | string): boolean {
    if (typeof result === "string") {
      setProblem(result)
      return false
    }
    setProblem(null)
    if (result.id > lastIdRef.current) {
      lastIdRef.current = result.id
      setMessages((current) => [ ...current, result ])
    }
    return true
  }

  // Asking it to land. The system turn it comes back with goes into the
  // transcript like any other, so the pane shows what was asked and when.
  async function wrapUp(): Promise<void> {
    const result = await wrapUpAgent(agentsUrl, agentId)
    if (typeof result === "string") {
      setProblem(result)
      return
    }
    setProblem(null)
    absorb(result)
  }

  // Optimistic: the turn appears the moment you hit Send, then reconciles
  // with the row the server created (or is marked failed if the runtime
  // refused it — in which case no row exists server-side either).
  async function send(): Promise<void> {
    const contents = draft.trim()
    if (!contents) return

    const temp: LocalTurn = {
      id: tempIdRef.current--,
      role: "user",
      kind: "chat",
      contents,
      summary: null,
      round_number: null,
      created_at: new Date().toISOString(),
    }
    setMessages((current) => [ ...current, temp ])
    setDraft("")
    setAwaitingReply(true)

    const result = await sendAgentMessage(agentsUrl, agentId, contents)
    if (typeof result === "string") {
      setProblem(result)
      setMessages((current) => current.map((m) => (m.id === temp.id ? { ...m, failed: true } : m)))
      // Nothing is thinking about a turn that was never delivered.
      setAwaitingReply(false)
      return
    }

    setProblem(null)
    if (result.id > lastIdRef.current) lastIdRef.current = result.id
    setMessages((current) => {
      const alreadyPolled = current.some((m) => m.id === result.id)
      return current.flatMap((m) => (m.id === temp.id ? (alreadyPolled ? [] : [ result ]) : [ m ]))
    })
  }

  if (missing) {
    return (
      <article className="workspace__doc">
        <header className="workspace__doc-head">
          <h1 className="workspace__doc-title">Agent not found</h1>
          <p className="workspace__doc-meta">
            <button type="button" className="workspace__inline-link" onClick={onBack}>Back to agents</button>
          </p>
        </header>
      </article>
    )
  }

  return (
    <article className="workspace__doc workspace__chat">
      <div className="workspace__doc workspace__chat-inner">
        <header className="workspace__doc-head workspace__doc-head--split">
          <div>
            <h1 className="workspace__doc-title">
              <button type="button" className="workspace__back" aria-label="Back to agents" onClick={onBack}>
                ←
              </button>
              {agent ? (
                  <AgentName
                      agent={agent}
                      agentsUrl={agentsUrl}
                      onChange={(updated) => {
                        setAgent(updated)
                        onAgentChanged?.(updated)
                      }}
                  />
              ) : ""}
            </h1>
          </div>

          {/* Everything about the agent that is not the conversation: the jobs
            it has running, and the one thing you can ask of it mid-round. A
            menu whose items are all greyed out is worse than no menu, so it
            appears only when at least one of them means something. */}
          {(running.length > 0 || presence?.status_live) && (
              <div className="workspace__menu">
                <button
                    type="button"
                    className="workspace__menu-toggle"
                    aria-label="Agent actions"
                    aria-expanded={menuOpen}
                    aria-haspopup="menu"
                    onClick={() => setMenuOpen((open) => !open)}
                >
                  ⋯
                </button>
                {menuOpen && (
                    <>
                      {/* Click anywhere to dismiss, the way the workspace's other
                    menus behave. */}
                      <div className="workspace__menu-scrim" role="presentation" onClick={() => setMenuOpen(false)} />
                      <div className="workspace__menu-list" role="menu">
                        {running.length > 0 && (
                            <button
                                type="button"
                                role="menuitem"
                                className="workspace__menu-item"
                                onClick={() => {
                                  setMenuOpen(false)
                                  setJobsOpen(true)
                                }}
                            >
                              View jobs
                              <span className="workspace__menu-badge">{running.length}</span>
                            </button>
                        )}
                        {presence?.status_live && (
                            <button
                                type="button"
                                role="menuitem"
                                className="workspace__menu-item"
                                title="Finish the current round without starting anything new"
                                onClick={() => {
                                  setMenuOpen(false)
                                  void wrapUp()
                                }}
                            >
                              Wrap up now
                            </button>
                        )}
                      </div>
                    </>
                )}
              </div>
          )}
        </header>

        {jobsOpen && (
            <JobsDialog
                jobs={running}
                failed={jobsFailed}
                fileUrl={fileUrl}
                agentsUrl={agentsUrl}
                agentId={agentId}
                onStopped={refreshJobs}
                onClose={() => setJobsOpen(false)}
            />
        )}

        <div className="workspace__chat-messages">
          {messages.length === 0 && (
              <p className="workspace__chat-empty">
                No messages yet. Start the agent, then say something.
              </p>
          )}
          {messages.map((message) => (
              <ChatTurn key={message.id} message={message} />
          ))}
          {statusLine && (
              <p className="workspace__chat-status" aria-live="polite">
                {statusLine}
                {/* The ellipsis is ours, not the agent's: it means "still going",
                which is a fact about the connection rather than about what was
                said. Keeping it out of the stored phrase is also what stops
                "Proving it...…" when a model adds its own. */}
                <span className="workspace__chat-status-dots" aria-hidden="true">…</span>
                {statusAge && <span className="workspace__chat-status-age"> · {statusAge}</span>}
              </p>
          )}
          <div ref={bottomRef} />
        </div>

        {problem && <p className="workspace__chat-problem" role="alert">{problem}</p>}

        <form
            className="workspace__chat-composer"
            onSubmit={(event) => {
              event.preventDefault()
              void send()
            }}
        >
          <input
              type="text"
              className="workspace__input"
              placeholder="Message this agent…"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
          />
          <button type="submit" className="btn btn--primary" disabled={!draft.trim()}>
            Send
          </button>
        </form>
      </div>
    </article>
  )
}

// What to put under the last message, or nothing.
//
// The server decides whether a reported status is still true (`status_live`) —
// the age of the status cannot say, because a round runs for minutes without
// changing it, so what settles it is whether the runtime is still heartbeating.
// Our own "Thinking" is the fallback for the gap between sending a turn and the
// runtime reporting its first status, which is the moment the pane would
// otherwise look broken.
function presenceLine(presence: AgentPresence | null, awaitingReply: boolean): string | null {
  if (presence?.status_live) return presence.status_detail || "Working"
  return awaitingReply ? "Thinking" : null
}

// How long the agent has been on this, once that is worth saying.
//
// A status answers "what", and the next question is always "why is this taking
// so long" — which nothing on the page could answer, because a phrase looks the
// same after four seconds and after four minutes. Under a minute it says
// nothing: everything is quick at first, and a counter starting from zero on
// every tool call is noise.
function elapsedSince(at: string | null): string | null {
  if (!at) return null

  const started = Date.parse(at)
  if (Number.isNaN(started)) return null

  const minutes = Math.floor((Date.now() - started) / 60_000)
  if (minutes < 1) return null
  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes - hours * 60}m`
}

function ChatTurn({ message }: { message: LocalTurn }): JSX.Element {
  if (message.role === "system") {
    return <p className="workspace__chat-system">{message.contents}</p>
  }

  const failed = message.failed ? " workspace__chat-turn--failed" : ""

  // Only what the user said is a bubble. An agent's turn is the document you
  // came to read — often a whole round's worth of reasoning — and boxing it at
  // 72% width to signal "not you" costs it the room it needs. Which side said
  // what is already obvious from the alternation.
  return (
    <div className={`workspace__chat-turn workspace__chat-turn--${message.role}${failed}`}>
      {message.role === "user" ? (
        <div className="workspace__chat-bubble" title={message.failed ? "Not delivered" : undefined}>
          <Prose text={message.contents} />
        </div>
      ) : (
        <div className="workspace__chat-said">
          <Prose text={message.contents} />
        </div>
      )}
    </div>
  )
}

// A turn, rendered as what it is. Both sides of the conversation are markdown
// with mathematics in it: an agent writes round reports in headings and lists,
// and a person asking about $\zeta(s)$ is writing the same notation the agent
// will answer in. Raw asterisks and dollar signs are the source of a document,
// not the document.
//
// Same plugins as MarkdownFile, and for the same reason — a formula has to look
// identical in the chat and in the findings file it ends up in. remark-math
// only recognizes dollar delimiters; \( \) and \[ \] pass through as text.
function Prose({ text }: { text: string | null }): JSX.Element | null {
  if (!text) return null

  return (
    <Markdown remarkPlugins={[ remarkMath ]} rehypePlugins={[ rehypeKatex ]}>
      {text}
    </Markdown>
  )
}

// Click-to-rename, persisted immediately — the same interaction as the
// workspace name in the rail. An empty field is a cancel.
function AgentName({
  agent,
  agentsUrl,
  onChange,
}: {
  agent: AgentRecord
  agentsUrl: string
  onChange: (agent: AgentRecord) => void
}): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(agent.name ?? "")

  function startEditing(): void {
    setDraft(agent.name ?? "")
    setEditing(true)
  }

  async function commit(): Promise<void> {
    setEditing(false)

    const next = draft.trim()
    if (!next || next === agent.name) return

    const updated = await updateAgent(agentsUrl, agent.id, { name: next })
    if (updated) onChange(updated)
  }

  if (!editing) {
    return (
      <button type="button" className="workspace__agent-rename" onClick={startEditing} title="Click to rename">
        {agent.name ?? "Unnamed agent"}
      </button>
    )
  }

  return (
    <input
      type="text"
      className="workspace__agent-name-input"
      aria-label="Agent name"
      value={draft}
      maxLength={120}
      autoFocus
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault()
          void commit()
        } else if (event.key === "Escape") {
          event.preventDefault()
          setEditing(false)
        }
      }}
    />
  )
}
